import { 
  collection, 
  doc, 
  getDoc, 
  getDocs,
  setDoc, 
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  orderBy,
  Timestamp,
  deleteField
} from 'firebase/firestore'
import { db, shouldSkipFirestore } from './config'
import { getCurrentUser } from './auth'
import { errorHandler } from '@/utils/errorHandler'
import { logger } from '@/utils/logger'

export interface ContactInfo {
  // Persönliche Informationen
  name: string                    // "MAXIMILIAN REITSBERGER"
  location: string                // "München, Deutschland"
  profession: string              // "Web-Entwicklung mit QA-Expertise"
  
  // Kontaktmöglichkeiten
  email: string                   // "kontakt@example.com"
  phone?: string                  // Optional: "+49 123 456789"
  phoneDisplay?: string           // Optional: Anzeige-Format "Mo-Fr, 9-18 Uhr"
  whatsapp?: string               // Optional: WhatsApp Business Link
  linkedin?: string               // Optional: LinkedIn Profil-URL
  github?: string                 // Optional: GitHub Profil-URL
  website?: string                // Optional: Website URL
  
  // Erreichbarkeit
  responseTime: string            // "Innerhalb von 24 Stunden"
  availability: string            // "Mo-Fr, 9-18 Uhr"
  
  // Zusätzliche Informationen
  benefits?: string[]            // Array von Vorteilen/Benefits
  // z.B. ["Kostenlose Erstberatung", "Unverbindliches Angebot", ...]
  
  // Meta-Daten
  updatedAt: Date
  updatedBy?: string               // User ID des letzten Bearbeiters
}

// Standard-Kontaktdaten
const DEFAULT_CONTACT_INFO: Omit<ContactInfo, 'updatedAt' | 'updatedBy'> = {
  name: 'MAXIMILIAN REITSBERGER',
  location: 'München, Deutschland',
  profession: 'Web-Entwicklung mit QA-Expertise',
  email: 'kontakt@example.com',
  responseTime: 'Innerhalb von 24 Stunden',
  availability: 'Mo-Fr, 9-18 Uhr',
  benefits: [
    'Kostenlose Erstberatung',
    'Unverbindliches Angebot',
    'Schnelle Antwort innerhalb von 24h',
    'Professionelle Umsetzung'
  ]
}

// Kontaktdaten abrufen
export const getContactInfo = async (): Promise<ContactInfo | null> => {
  // Für Bots/Performance-Tools: Keine Firestore-Abfrage, um Timeouts zu vermeiden
  if (shouldSkipFirestore || !db) {
    logger.debug('Firestore übersprungen - Kontaktdaten werden nicht geladen');
    return null;
  }
  
  try {
    const contactRef = doc(db, 'contactInfo', 'main')
    const contactSnap = await getDoc(contactRef)
    
    if (!contactSnap.exists()) {
      return null
    }
    
    const data = contactSnap.data()
    return {
      ...data,
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as ContactInfo
  } catch (error) {
    errorHandler.handle(error, 'getContactInfo')
    return null
  }
}

// Kontaktdaten aktualisieren
export const updateContactInfo = async (data: Partial<ContactInfo>): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const user = getCurrentUser()
    if (!user) {
      throw new Error('Nicht authentifiziert')
    }
    
    const contactRef = doc(db, 'contactInfo', 'main')
    const existingDoc = await getDoc(contactRef)
    
    const updateData: Record<string, any> = {
      ...data,
      updatedAt: Timestamp.now(),
      updatedBy: user.uid
    }
    
    // Entferne undefined Werte
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        delete updateData[key]
      }
    })
    
    if (existingDoc.exists()) {
      await updateDoc(contactRef, updateData)
    } else {
      // Erstelle Dokument mit Standard-Werten falls nicht vorhanden
      await setDoc(contactRef, {
        ...DEFAULT_CONTACT_INFO,
        ...updateData
      })
    }
  } catch (error) {
    errorHandler.handle(error, 'updateContactInfo')
    throw error
  }
}

// Standard-Kontaktdaten erstellen (einmalig)
export const createDefaultContactInfo = async (): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const user = getCurrentUser()
    if (!user) {
      throw new Error('Nicht authentifiziert')
    }
    
    const contactRef = doc(db, 'contactInfo', 'main')
    const existingDoc = await getDoc(contactRef)
    
    if (existingDoc.exists()) {
      // Bereits vorhanden, nichts tun
      return
    }
    
    await setDoc(contactRef, {
      ...DEFAULT_CONTACT_INFO,
      updatedAt: Timestamp.now(),
      updatedBy: user.uid
    })
  } catch (error) {
    errorHandler.handle(error, 'createDefaultContactInfo')
    throw error
  }
}

// Standard-Kontaktdaten zurückgeben (für Fallback)
export const getDefaultContactInfo = (): Omit<ContactInfo, 'updatedAt' | 'updatedBy'> => {
  return DEFAULT_CONTACT_INFO
}

// Kontaktformular-Submission Interface
export interface ContactSubmission {
  name: string
  email: string
  interest?: string
  projectType?: string
  budget?: string
  message: string
  privacyAccepted: boolean
  submittedAt: Date
  ipAddress?: string
  userAgent?: string
  isSpam?: boolean
  spamReason?: string
  isRead?: boolean
  isProcessed?: boolean
  processedAt?: Date
  processedBy?: string
}

// Kontaktformular-Submission speichern
export const submitContactForm = async (data: {
  name: string
  email: string
  interest?: string
  projectType?: string
  budget?: string
  message: string
  privacyAccepted: boolean
}): Promise<string> => {
  // Für Bots/Performance-Tools: Keine Firestore-Abfrage, um Timeouts zu vermeiden
  if (shouldSkipFirestore || !db) {
    logger.debug('Firestore übersprungen - Kontaktformular wird nicht gespeichert');
    throw new Error('Kontaktformular ist nicht verfügbar');
  }
  
  try {
    // Normalisiere E-Mail-Adresse (lowercase) um Duplikate zu vermeiden
    const normalizedEmail = (data.email || '').toLowerCase().trim()
    
    const submissionData: Omit<ContactSubmission, 'submittedAt'> & { submittedAt: Timestamp } = {
      ...data,
      email: normalizedEmail, // Verwende normalisierte E-Mail-Adresse
      submittedAt: Timestamp.now(),
      isSpam: false
    }

    const submissionsRef = collection(db, 'contactSubmissions')
    const docRef = await addDoc(submissionsRef, submissionData)
    
    return docRef.id
  } catch (error) {
    errorHandler.handle(error, 'submitContactForm')
    throw error
  }
}

// Alle Kontaktformular-Submissions abrufen (nur für Admin)
export const getAllContactSubmissions = async (): Promise<(ContactSubmission & { id: string })[]> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const user = getCurrentUser()
    if (!user) {
      throw new Error('Nicht authentifiziert')
    }

    const submissionsRef = collection(db, 'contactSubmissions')
    const q = query(submissionsRef, orderBy('submittedAt', 'desc'))
    const querySnapshot = await getDocs(q)
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data()
      return {
        id: doc.id,
        ...data,
        submittedAt: data.submittedAt?.toDate() || new Date(),
        processedAt: data.processedAt?.toDate() || undefined
      } as ContactSubmission & { id: string }
    })
  } catch (error) {
    errorHandler.handle(error, 'getAllContactSubmissions')
    throw error
  }
}

// Kontaktformular-Submission löschen (nur für Admin)
export const deleteContactSubmission = async (submissionId: string): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const user = getCurrentUser()
    if (!user) {
      throw new Error('Nicht authentifiziert')
    }

    const submissionRef = doc(db, 'contactSubmissions', submissionId)
    await deleteDoc(submissionRef)
  } catch (error) {
    errorHandler.handle(error, 'deleteContactSubmission')
    throw error
  }
}

// Kontaktformular-Submission als Spam markieren (nur für Admin)
export const markSubmissionAsSpam = async (submissionId: string, reason?: string): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const user = getCurrentUser()
    if (!user) {
      throw new Error('Nicht authentifiziert')
    }

    const submissionRef = doc(db, 'contactSubmissions', submissionId)
    await updateDoc(submissionRef, {
      isSpam: true,
      spamReason: reason || 'Manuell als Spam markiert'
    })
  } catch (error) {
    errorHandler.handle(error, 'markSubmissionAsSpam')
    throw error
  }
}

// Kontaktformular-Submission als bearbeitet/gelesen markieren (nur für Admin)
export const markSubmissionAsProcessed = async (submissionId: string): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const user = getCurrentUser()
    if (!user) {
      throw new Error('Nicht authentifiziert')
    }

    const submissionRef = doc(db, 'contactSubmissions', submissionId)
    await updateDoc(submissionRef, {
      isRead: true,
      isProcessed: true,
      processedAt: Timestamp.now(),
      processedBy: user.uid
    })
  } catch (error) {
    errorHandler.handle(error, 'markSubmissionAsProcessed')
    throw error
  }
}

// Kontaktformular-Submission als ungelesen markieren (nur für Admin)
export const markSubmissionAsUnread = async (submissionId: string): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const user = getCurrentUser()
    if (!user) {
      throw new Error('Nicht authentifiziert')
    }

    const submissionRef = doc(db, 'contactSubmissions', submissionId)
    await updateDoc(submissionRef, {
      isRead: false,
      isProcessed: false,
      processedAt: deleteField(),
      processedBy: deleteField()
    })
  } catch (error) {
    errorHandler.handle(error, 'markSubmissionAsUnread')
    throw error
  }
}

// Mehrere Submissions als bearbeitet markieren (Bulk-Aktion)
export const markSubmissionsAsProcessed = async (submissionIds: string[]): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  // Lokale Variable für TypeScript-Narrowing
  const firestore = db
  
  try {
    const user = getCurrentUser()
    if (!user) {
      throw new Error('Nicht authentifiziert')
    }

    const batch = await Promise.all(
      submissionIds.map(id => {
        const submissionRef = doc(firestore, 'contactSubmissions', id)
        return updateDoc(submissionRef, {
          isRead: true,
          isProcessed: true,
          processedAt: Timestamp.now(),
          processedBy: user.uid
        })
      })
    )
    
    await Promise.all(batch)
  } catch (error) {
    errorHandler.handle(error, 'markSubmissionsAsProcessed')
    throw error
  }
}

// Mehrere Submissions löschen (Bulk-Aktion)
export const deleteContactSubmissions = async (submissionIds: string[]): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  // Lokale Variable für TypeScript-Narrowing
  const firestore = db
  
  try {
    const user = getCurrentUser()
    if (!user) {
      throw new Error('Nicht authentifiziert')
    }

    const batch = await Promise.all(
      submissionIds.map(id => {
        const submissionRef = doc(firestore, 'contactSubmissions', id)
        return deleteDoc(submissionRef)
      })
    )
    
    await Promise.all(batch)
  } catch (error) {
    errorHandler.handle(error, 'deleteContactSubmissions')
    throw error
  }
}

// Alle Submissions löschen (nur für Admin)
export const deleteAllContactSubmissions = async (): Promise<number> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const user = getCurrentUser()
    if (!user) {
      throw new Error('Nicht authentifiziert')
    }

    const submissionsRef = collection(db, 'contactSubmissions')
    const querySnapshot = await getDocs(submissionsRef)
    
    const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref))
    await Promise.all(deletePromises)
    
    return querySnapshot.size
  } catch (error) {
    errorHandler.handle(error, 'deleteAllContactSubmissions')
    throw error
  }
}

